import React from 'react';
import { InsightItem, Opportunity, InternalOwnership, SuccessMetric } from '@/types/journey';
import { Button } from '@/components/ui/button';
import { Plus, FileText, Users, BarChart, X } from 'lucide-react';
import OpportunityField from '../stage/OpportunityField';
import InternalOwnershipField from './InternalOwnershipField';
import SuccessMetricsField from './SuccessMetricsField';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DynamicInsightFieldProps {
  insightItems: InsightItem[];
  onInsightUpdate: (insightItems: InsightItem[]) => void;
}

// Define the field type order
const FIELD_TYPE_ORDER = {
  'opportunities': 1,
  'internalOwnership': 2,
  'successMetrics': 3
};

// Field type options - ordered for the dropdown menu
const INSIGHT_FIELD_TYPES = [
  { id: 'opportunities', label: 'Opportunities', icon: <FileText className="h-4 w-4" /> },
  { id: 'internalOwnership', label: 'Internal Ownership', icon: <Users className="h-4 w-4" /> },
  { id: 'successMetrics', label: 'Success Metrics', icon: <BarChart className="h-4 w-4" /> },
];

const DynamicInsightField: React.FC<DynamicInsightFieldProps> = ({
  insightItems,
  onInsightUpdate
}) => {
  // Add a new insight field
  const handleAddInsightField = (type: 'opportunities' | 'internalOwnership' | 'successMetrics') => {
    console.log('handleAddInsightField called with type:', type);
    let newContent: any;
    
    switch (type) {
      case 'opportunities':
        newContent = [] as Opportunity[];
        break;
      case 'internalOwnership':
        newContent = [] as InternalOwnership[];
        break;
      case 'successMetrics':
        newContent = [] as SuccessMetric[];
        break;
      default:
        newContent = [];
        break;
    }
    
    const newItem: InsightItem = {
      id: `insight-${type}-${Date.now()}`,
      type,
      content: newContent
    };
    
    console.log('Adding new insight item:', newItem);
    console.log('Current insightItems:', insightItems);
    
    const updatedItems = [...insightItems, newItem];
    console.log('Updated insightItems:', updatedItems);
    
    onInsightUpdate(updatedItems);
  };
  
  // Remove an insight field
  const handleRemoveInsightField = (id: string) => {
    onInsightUpdate(insightItems.filter(item => item.id !== id));
  };
  
  // Update an insight field
  const handleUpdateInsightField = (id: string, content: any) => {
    const updatedItems = insightItems.map(item => {
      if (item.id === id) {
        return { ...item, content };
      }
      return item;
    });
    
    onInsightUpdate(updatedItems);
  };

  // Check if a field type is already in use
  const isFieldTypeInUse = (type: string) => {
    return insightItems.some(item => item.type === type);
  };

  // Render the appropriate field based on type
  const renderInsightField = (item: InsightItem) => {
    switch (item.type) {
      case 'opportunities':
        return (
          <div className="relative group" key={item.id}>
            <OpportunityField
              label="Opportunities"
              opportunities={Array.isArray(item.content) ? item.content : []}
              onChange={(opportunities) => handleUpdateInsightField(item.id, opportunities)}
              placeholder="Click to add potential improvements and opportunities..."
            />
            <button
              type="button"
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleRemoveInsightField(item.id)}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        );
      
      case 'internalOwnership':
        return (
          <div className="relative group" key={item.id}>
            <InternalOwnershipField
              label="Internal Ownership"
              ownership={Array.isArray(item.content) ? item.content : []}
              onChange={(ownership) => handleUpdateInsightField(item.id, ownership)}
              placeholder="Click to add team responsibilities..."
            />
            <button
              type="button"
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleRemoveInsightField(item.id)}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        );
      
      case 'successMetrics':
        return (
          <div className="relative group" key={item.id}>
            <SuccessMetricsField
              label="Success Metrics"
              metrics={Array.isArray(item.content) ? item.content : []}
              onChange={(metrics) => handleUpdateInsightField(item.id, metrics)}
              placeholder="Click to add key success metrics..."
            />
            <button
              type="button"
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleRemoveInsightField(item.id)}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Sort insight items according to the defined order
  const sortedInsightItems = [...insightItems].sort((a, b) => {
    const orderA = FIELD_TYPE_ORDER[a.type] || 999;
    const orderB = FIELD_TYPE_ORDER[b.type] || 999;
    return orderA - orderB;
  });

  return (
    <div className="space-y-3 flex-1">
      {/* Insight fields - sorted by defined order */}
      {sortedInsightItems.map(renderInsightField)}
      
      {/* Add field button */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full border border-dashed border-muted-foreground/20 bg-muted/20 hover:bg-muted/40 text-xs justify-center py-1 h-auto"
            onClick={() => console.log('Add insight field button clicked')}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add insight field
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {INSIGHT_FIELD_TYPES.map((fieldType) => {
            const isDisabled = isFieldTypeInUse(fieldType.id);
            return (
              <DropdownMenuItem
                key={fieldType.id}
                disabled={isDisabled}
                onSelect={() => {
                  console.log(`Selected field type: ${fieldType.id}`);
                  if (!isDisabled) {
                    handleAddInsightField(fieldType.id as any);
                  }
                }}
                className={isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <div className="flex items-center">
                  {fieldType.icon}
                  <span className="ml-2">{fieldType.label}</span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default DynamicInsightField; 